import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Pagination } from "@/organism";
import { ToolFilter } from "@/organism/BossBaby";
import { BossBabyToolDto, BossBabySearchParams } from "@/dto/BossBabyDto";
import { format } from "date-fns";

interface ToolListTemplateProps {
  tools: BossBabyToolDto[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSearch: (params: BossBabySearchParams) => void;
  onEdit: (toolId: number) => void;
  onDelete: (toolId: number) => void;
  onCreate: () => void;
}

export const ToolListTemplate: React.FC<ToolListTemplateProps> = ({
  tools,
  loading,
  total,
  page,
  limit,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState<boolean | "">("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSearch = useCallback(() => {
    const params: BossBabySearchParams = {
      search: searchTerm.trim() || undefined,
      category: category || undefined,
      isActive: isActive === "" ? undefined : isActive,
      sortBy: sortBy as "name" | "createdAt" | "priority",
      sortOrder,
    };
    onSearch(params);
  }, [searchTerm, category, isActive, sortBy, sortOrder, onSearch]);

  const handleReset = useCallback(() => {
    setSearchTerm("");
    setCategory("");
    setIsActive("");
    setSortBy("name");
    setSortOrder("asc");
    onSearch({});
  }, [onSearch]);

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            <CircularProgress size={30} />
          </TableCell>
        </TableRow>
      );
    }

    if (tools.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <Typography variant="h6" color="text.secondary">
                No Boss Baby Tools found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first Boss Baby Tool to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreate}
                size="small"
              >
                Create Tool
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return tools.map((tool) => (
      <TableRow key={tool.toolId} hover>
        <TableCell align="right">{tool.toolId}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {tool.toolName}
          </Typography>
          {tool.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tool.description}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip label={tool.category} size="small" />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={tool.priorityLevel}
            size="small"
            color={
              tool.priorityLevel === "HIGH"
                ? "error"
                : tool.priorityLevel === "MEDIUM"
                ? "warning"
                : "default"
            }
          />
        </TableCell>
        <TableCell align="right">{tool.timeoutSeconds}s</TableCell>
        <TableCell align="center">
          <Chip
            label={tool.isActive ? "Active" : "Inactive"}
            size="small"
            color={tool.isActive ? "success" : "default"}
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(tool.createdAt), "yyyy-MM-dd")}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onEdit(tool.toolId)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(tool.toolId)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Boss Baby Tools
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage Boss Baby tools and their configurations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          size="large"
        >
          Create Tool
        </Button>
      </Box>

      {/* Search & Filter */}
      <ToolFilter
        searchTerm={searchTerm}
        category={category}
        isActive={isActive}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearchTermChange={setSearchTerm}
        onCategoryChange={setCategory}
        onIsActiveChange={setIsActive}
        onSortByChange={setSortBy}
        onSortOrderChange={setSortOrder}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? "Loading..." : `Showing ${tools.length} of ${total} tools`}
        </Typography>
      </Box>

      {/* Tool Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="right" sx={{ width: 80 }}>
                ID
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>Tool Name</TableCell>
              <TableCell sx={{ width: 120 }}>Category</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Priority
              </TableCell>
              <TableCell align="right" sx={{ width: 100 }}>
                Timeout
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Status
              </TableCell>
              <TableCell sx={{ width: 120 }}>Created</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableContent()}</TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && tools.length > 0 && (
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={total}
            page={page - 1}
            rowsPerPage={limit}
            onPageChange={(_, newPage) => onPageChange(newPage + 1)}
            onRowsPerPageChange={(e) =>
              onRowsPerPageChange(parseInt(e.target.value, 10))
            }
          />
        </Box>
      )}
    </Container>
  );
};
